<template>
  <div class="rechnung-page">
    <div v-if="isAuthLoading" class="state-block" data-variant="auth">
      <v-progress-circular indeterminate color="#EA5545" size="64" />
      <p>{{ t('rechnung.auth.checking') }}</p>
    </div>

    <div v-else-if="needsLogin" class="state-block" data-variant="auth" data-state="empty">
      <v-icon size="64" color="#EA5545">mdi-account-lock</v-icon>
      <h2>{{ t('rechnung.auth.loginTitle') }}</h2>
      <p>{{ t('rechnung.auth.loginDescription') }}</p>
      <router-link to="/login">
        <v-btn color="#EA5545" variant="flat" prepend-icon="mdi-login">
          {{ t('rechnung.auth.loginButton') }}
        </v-btn>
      </router-link>
    </div>

    <template v-else>
    <!-- Contract Info Card - Prominent at top -->
    <v-card v-if="tarif" class="contract-info-card" elevation="2">
      <div class="contract-info-content">
        <div class="contract-info-icon">
          <v-icon size="32" color="#EA5545">mdi-file-document-check</v-icon>
        </div>
        <div class="contract-info-main">
          <div class="contract-info-title">{{ tarif.un_name }}</div>
          <div class="contract-info-meta">
            <router-link
              v-if="contractBreadcrumbLink"
              :to="contractBreadcrumbLink"
              class="contract-info-link"
            >
              <v-icon size="14" class="mr-1">mdi-arrow-left</v-icon>
              Zurück zum Vertrag
            </router-link>
          </div>
        </div>
      </div>
    </v-card>

    <div class="page-header">
      <div class="page-title-block">
        <h1>{{ t('rechnung.pageTitle') }}</h1>
        <p class="page-subtitle">
          {{ t('rechnung.pageSubtitle') }}
        </p>
      </div>
    </div>

    <RechnungCategoryOne
      v-if="showCategoryOne"
      :tarif="tarif"
      :pdf-url-rechnung="pdfUrlRechnung"
      :pdf-url-heilkostenplan="pdfUrlHeilkostenplan"
      :email-href-rechnung="emailHrefRechnung"
      :email-href-heilkostenplan="emailHrefHeilkostenplan"
      :project-name="projectName"
    />
    
    <RechnungCategoryTwo
      v-else-if="showCategoryTwo"
      :tarif="tarif"
      :pdf-url-rechnung="pdfUrlRechnung"
      :email-href-rechnung="emailHrefRechnung"
      :project-name="projectName"
    />
    
    <div v-else class="delivery-placeholder">
      <v-alert type="info" variant="tonal">
        Für diesen Tarif steht aktuell keine spezifische Einreichungsvorlage zur Verfügung.
      </v-alert>
    </div>

    <v-card class="cta-card" elevation="1">
      <div class="cta-text">
        <h3>{{ t('rechnung.ctaTitle') }}</h3>
        <p>
          {{ t('rechnung.ctaDescription') }}
        </p>
    </div>
      <router-link :to="helpLinkForTarif">
        <v-btn color="#EA5545" variant="flat" prepend-icon="mdi-shield-account">
          {{ t('rechnung.ctaButton') }}
        </v-btn>
      </router-link>
    </v-card>
    </template>
  </div>
</template>

<script setup>
import { ref, inject, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import RechnungCategoryOne from '@/components/objects/RechnungCategoryOne.vue'
import RechnungCategoryTwo from '@/components/objects/RechnungCategoryTwo.vue'
import { useLanguage } from '@/composables/useLanguage'
import { CLEARMENT_TITLE, CLEARMENT_DESCRIPTION } from '@/constants/documentHead'

const baseCalls = inject('baseCalls')
const userStore = inject('userStore')
const errorStore = inject('errorStore')
const { t } = useLanguage()
const updateDocumentHead = inject('updateDocumentHead')
updateDocumentHead?.(CLEARMENT_TITLE, CLEARMENT_DESCRIPTION)

const route = useRoute()

const { userData } = storeToRefs(userStore)

const tarif = ref(null)
const productCategory = ref(null)
const emailtitle = ref('')
const emailtext = ref('')
const emailtitlerechnung = ref('')
const emailtextrechnung = ref('')

const projectName = 'Clearment'

const user = computed(() => userData.value)
const isAuthLoading = ref(true)
const needsLogin = ref(false)

const pdfUrlRechnung = computed(() => {
  const rawId = route.params.tarifId
  const mypId = Number(rawId)
  if (!rawId || Number.isNaN(mypId) || mypId <= 0) return null
  return `/vorlage/${mypId}/rechnung`
})

const pdfUrlHeilkostenplan = computed(() => {
  const rawId = route.params.tarifId
  const mypId = Number(rawId)
  if (!rawId || Number.isNaN(mypId) || mypId <= 0) return null
  return `/vorlage/${mypId}/heilkostenplan`
})

// const contractBreadcrumbName = computed(() => {
//   if (!tarif.value) return ''
//   return (
//     tarif.value.un_name ||
//     tarif.value.prod_name ||
//     (route.params.tarifId ? `#${route.params.tarifId}` : '')
//   )
// })

const contractBreadcrumbLink = computed(() => {
  const rawId = route.params.tarifId
  const mypId = Number(rawId)
  if (!rawId || Number.isNaN(mypId) || mypId <= 0) {
    return null
  }
  return {
    name: 'vertrag',
    params: { id: mypId }
  }
})

const helpLinkForTarif = computed(() => {
  const rawId = route.params.tarifId
  const mypId = Number(rawId)
  if (!rawId || Number.isNaN(mypId) || mypId <= 0) {
    return { name: 'help' }
  }
  return { name: 'help', params: { id: mypId } }
})

const showCategoryOne = computed(() => {
  const cat = productCategory.value ?? tarif.value?.prod_category ?? 0
  return Number(cat) === 1
})

const showCategoryTwo = computed(() => {
  const cat = productCategory.value ?? tarif.value?.prod_category ?? 0
  return Number(cat) === 2
})

const emailHrefRechnung = computed(() => {
  if (!tarif.value || !tarif.value.un_rechnung_email) return null
  const to = tarif.value.un_rechnung_email
  const subject = encodeURIComponent(emailtitlerechnung.value || '')
  const body = encodeURIComponent(emailtextrechnung.value || '')
  return `mailto:${to}?subject=${subject}&body=${body}`
})

const emailHrefHeilkostenplan = computed(() => {
  if (!tarif.value || !tarif.value.un_rechnung_email) return null
  const to = tarif.value.un_rechnung_email
  const subject = encodeURIComponent(emailtitle.value || '')
  const body = encodeURIComponent(emailtext.value || '')
  return `mailto:${to}?subject=${subject}&body=${body}`
})

function setTexte() {
  if (!user.value) return

  emailtitlerechnung.value =
    'Einreichung Rechnung ' + (user.value.rechnung_name || '')
  emailtextrechnung.value =
    'Sehr geehrte Damen und Herren, bitte erstatten Sie meine Rechnung/en tarifgemäß auf mein bei Ihnen gespeichertes Beitragskonto. Meine Versicherungsnummer: ' +
    (user.value.myp_vertrags_nummer || '') +
    '    Versicherte Person: ' +
    (user.value.user_vp_vorname || '') +
    ' ' +
    (user.value.user_vp_name || '') +
    ' Ich bin Nutzer des Clearment Vertragsschutz (clearment.de). Ihre Erstattungsqualität  und Geschwindigkeit wird über Clearment  positiv bewertet. Mit freundlichen Grüßen ' +
    (user.value.rechnung_name || '')

  emailtitle.value =
    'Prüfung Heil- und Kostenplan ' + (user.value.rechnung_name || '')
  emailtext.value =
    'Sehr geehrte Damen und Herren, bitte prüfen Sie für den beiliegenden HKP mit welcher Erstattungsleistung zu rechnen ist und senden Sie mir eine schriftliche Kostenerstattungszusage zu. Meine Versicherungsnummer: ' +
    (user.value.myp_vertrags_nummer || '') +
    '    Versicherte Person: ' +
    (user.value.user_vp_name || '') +
    ' Ich bin Nutzer des Clearment Vertragsschutz (clearment.de). Ihre Erstattungsqualität  und Geschwindigkeit wird über Clearment  positiv bewertet. Mit freundlichen Grüßen ' +
    (user.value.rechnung_name || '')
}

async function loadTarifForRoute() {
  const rawId = route.params.tarifId
  if (!rawId && rawId !== 0) {
    tarif.value = null
    return
  }

  const mypId = Number(rawId)
  if (Number.isNaN(mypId) || mypId <= 0) {
    tarif.value = null
    return
  }

  try {
    const response = await baseCalls.clearmentPost({
      action: 'gettarifformyproduct',
      myp_id: mypId
    })

    if (response.data.success && response.data.tarif) {
      tarif.value = response.data.tarif
      productCategory.value =
        response.data.product?.prod_category ??
        response.data.tarif?.prod_category ??
        null
    } else {
      tarif.value = null
      productCategory.value = null
      if (errorStore && typeof errorStore.setError === 'function') {
        errorStore.setError(
          true,
          response.data.message ||
            'Tarifdaten konnten für diesen Vertrag nicht geladen werden'
        )
      }
    }
  } catch (err) {
    console.error('Error loading tarif for product:', err)
    tarif.value = null
    productCategory.value = null
    if (errorStore && typeof errorStore.setError === 'function') {
      errorStore.setError(
        true,
        'Beim Laden der Tarifdaten ist ein Fehler aufgetreten'
      )
    }
  }
}

async function ensureLogin() {
  isAuthLoading.value = true
  needsLogin.value = false
  try {
    if (!userData.value && baseCalls?.userGet) {
      const response = await baseCalls.userGet({ action: 'loggeduser', id: 0 })
      if (response?.data?.user) {
        userStore?.setUser?.(response.data.user)
      }
    }
    if (!userData.value) {
      needsLogin.value = true
      tarif.value = null
      productCategory.value = null
    }
  } catch (error) {
    console.error('ensureLogin rechnung', error)
  } finally {
    isAuthLoading.value = false
  }
}

onMounted(() => {
  ensureLogin().then(() => {
    if (user.value) {
      setTexte()
    }
    if (!needsLogin.value) {
      loadTarifForRoute()
    }
  })
})

watch(userData, (newUser) => {
  if (newUser) {
    needsLogin.value = false
    setTexte()
    loadTarifForRoute()
  }
})

watch(
  () => route.params.tarifId,
  () => {
    if (!needsLogin.value) {
      loadTarifForRoute()
    }
  }
)
</script>

<style scoped src="@/assets/styles/sfc/components/sites/RechnungIndex.css"></style>
